import { inventoryTypeUIDs } from "@/global/config/constants";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import {
  InventoryType,
  SectionLevelInventory,
} from "@/global/store/data/types";
import { SectionLevelInventoryCard } from "./SectionLevelInventoryCard";
import { SeatLevelInventoryCard } from "./SeatLevelInventoryCard";
import { SeatLevelMultiInventoryCard } from "./SeatLevelMultiInventoryCard";
import { SuiteLevelInventoryCard } from "./SuiteLevelInventoryCard";
import React from "react";
import { AnimatedCardProps } from "@/shared/base/components/AnimatedCard/AnimatedCard";
import { SuiteLevelV2InventoryCard } from "./SuiteLevelV2InventoryCard";
import { GroupLevelInventoryCard } from "./GroupLevelInventoryCard";

interface BaseInventoryCardProps {
  cardVariant?: AnimatedCardProps["variant"];
  inventory: InventoryType;
  isSelected?: boolean;
  isDisabled?: boolean;
  isDisabledHoverAnim?: boolean;
  isFooterHidden?: boolean;
  onSelect?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onRender?: (el: HTMLElement) => void;
  style?: AnimatedCardProps["style"];
  onClickViewSeat?: (section: string) => void;
}

export function BaseInventoryCard(props: BaseInventoryCardProps) {
  const { inventoryType: configInventoryType, seatMapConfigs } = useTeamConfig(); // prettier-ignore
  const isSeatMapUnavailable = seatMapConfigs?.isView3dButtonHidden || false;
  const isView3dButtonHiddenForSection =
    seatMapConfigs?.isView3dButtonHiddenBySectionName?.includes(
      (props.inventory as SectionLevelInventory)?.displaySectionName,
    ) || false;

  return (
    <>
      {configInventoryType == inventoryTypeUIDs.sectionLevel ? (
        <SectionLevelInventoryCard
          cardVariant={props?.cardVariant}
          inventory={props.inventory}
          isDisabled={props?.isDisabled && !props?.isSelected}
          isDisabledHoverAnim={props?.isDisabledHoverAnim}
          isFooterHidden={props?.isFooterHidden}
          isSelected={props?.isSelected}
          onSelect={props?.onSelect}
          onMouseEnter={props?.onMouseEnter}
          onMouseLeave={props?.onMouseLeave}
          onRender={props?.onRender}
          style={props?.style}
          hideViewSeatButton={isSeatMapUnavailable}
          onClickViewSeat={props?.onClickViewSeat}
        />
      ) : null}

      {configInventoryType == inventoryTypeUIDs.seatLevel ? (
        <SeatLevelInventoryCard
          cardVariant={props?.cardVariant}
          inventory={props.inventory}
          isDisabled={props?.isDisabled && !props?.isSelected}
          isDisabledHoverAnim={props?.isDisabledHoverAnim}
          isFooterHidden={props?.isFooterHidden}
          isSelected={props?.isSelected}
          onSelect={props?.onSelect}
          onMouseEnter={props?.onMouseEnter}
          onMouseLeave={props?.onMouseLeave}
          onRender={props?.onRender}
          style={props?.style}
          hideViewSeatButton={
            isSeatMapUnavailable || isView3dButtonHiddenForSection
          }
          onClickViewSeat={props?.onClickViewSeat}
        />
      ) : null}

      {configInventoryType == inventoryTypeUIDs.seatLevelMulti ? (
        <SeatLevelMultiInventoryCard
          cardVariant={props?.cardVariant}
          inventory={props.inventory}
          isDisabled={props?.isDisabled && !props?.isSelected}
          isDisabledHoverAnim={props?.isDisabledHoverAnim}
          isFooterHidden={props?.isFooterHidden}
          isSelected={props?.isSelected}
          onSelect={props?.onSelect}
          onMouseEnter={props?.onMouseEnter}
          onMouseLeave={props?.onMouseLeave}
          onRender={props?.onRender}
          style={props?.style}
          hideViewSeatButton={isSeatMapUnavailable}
          onClickViewSeat={props?.onClickViewSeat}
        />
      ) : null}

      {configInventoryType == inventoryTypeUIDs.suitLevel ? (
        <SuiteLevelInventoryCard
          cardVariant={props?.cardVariant}
          inventory={props.inventory}
          isDisabled={props?.isDisabled && !props?.isSelected}
          isDisabledHoverAnim={props?.isDisabledHoverAnim}
          isFooterHidden={props?.isFooterHidden}
          isSelected={props?.isSelected}
          onSelect={props?.onSelect}
          onMouseEnter={props?.onMouseEnter}
          onMouseLeave={props?.onMouseLeave}
          onRender={props?.onRender}
          style={props?.style}
        />
      ) : null}

      {configInventoryType == inventoryTypeUIDs.suiteLevelV2 ? (
        <SuiteLevelV2InventoryCard
          cardVariant={props?.cardVariant}
          inventory={props.inventory}
          isDisabled={props?.isDisabled && !props?.isSelected}
          isDisabledHoverAnim={props?.isDisabledHoverAnim}
          isFooterHidden={props?.isFooterHidden}
          isSelected={props?.isSelected}
          onSelect={props?.onSelect}
          onMouseEnter={props?.onMouseEnter}
          onMouseLeave={props?.onMouseLeave}
          onRender={props?.onRender}
          style={props?.style}
        />
      ) : null}

      {configInventoryType == inventoryTypeUIDs.groupLevel ? (
        <GroupLevelInventoryCard
          cardVariant={props?.cardVariant}
          inventory={props.inventory}
          isDisabled={props?.isDisabled && !props?.isSelected}
          isDisabledHoverAnim={props?.isDisabledHoverAnim}
          isFooterHidden={props?.isFooterHidden}
          isSelected={props?.isSelected}
          onSelect={props?.onSelect}
          onMouseEnter={props?.onMouseEnter}
          onMouseLeave={props?.onMouseLeave}
          onRender={props?.onRender}
          style={props?.style}
        />
      ) : null}
    </>
  );
}
